import { SafeParseError, SafeParseSuccess, ZodIssue } from 'zod';

import { OptionItemObject } from '../../../shared_models/interfaces';
import { CsvOptionRow, HEADER_ID_OPTION_NAME, ParsedCsv, ValidationResult } from '../model';

import { getParsedCsvZod, ParsedCsvZodType } from './ParsedCsv.zod';
import { getInvalidValue, Validator } from './Validator';

export class CsvValidator implements Validator<ParsedCsv, ParsedCsv, ParsedCsvZodType> {
    getStatistics(input: ParsedCsv): { toAdd: number; toUpdate: number } {
        const toUpdate = countRowsWithIds(input.rows);
        return {
            toUpdate,
            toAdd: input.rows.length - toUpdate,
        };
    }

    isSuccess(
        validationResult: SafeParseError<ParsedCsvZodType> | SafeParseSuccess<ParsedCsvZodType>
    ): validationResult is SafeParseSuccess<ParsedCsvZodType> {
        return validationResult.success;
    }

    convertToReadableString(
        validationResult: SafeParseError<ParsedCsvZodType> | SafeParseSuccess<ParsedCsvZodType>
    ): ValidationResult {
        if (this.isSuccess(validationResult)) {
            return { success: true, errors: [] };
        } else {
            const errors = validationResult.error.issues.map(item => ({
                message: item.message,
                path: convertToStringPath(item.path, item),
                invalidValue: getInvalidValue(item),
            }));
            return { success: false, errors };
        }
    }

    validate(data: ParsedCsv, treeData: OptionItemObject[]): ValidationResult {
        const result = getParsedCsvZod(treeData).safeParse(data);

        return {
            ...this.convertToReadableString(result),
            statistics: this.getStatistics(data),
        };
    }
}

const convertToStringPath = (path: (string | number)[], issue: ZodIssue): string => {
    return path.filter(item => item !== 'options' && item !== 'headers').join(', ');
};

const countRowsWithIds = (rows: CsvOptionRow[]) => {
    return rows.filter(row => !!row[HEADER_ID_OPTION_NAME]).length;
};

const csvValidator = new CsvValidator();
export default csvValidator;
